import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Copy,
  ExternalLink,
  Loader2,
  MessageCircle,
  Share2,
  ShieldCheck,
  Trash2,
} from 'lucide-react';

type ExistingShareLink = {
  id: string;
  token?: string;
  entity_slug: string;
  entity_id: string;
  label: string | null;
  expires_at: string;
  view_count: number;
  max_views: number | null;
  is_active: boolean;
  metadata?: unknown;
};

type ShareLinksPage = {
  items: ExistingShareLink[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
};

type CreateShareResponse = ExistingShareLink & {
  shareUrl: string;
};

type EntityFieldOption = {
  name: string;
  label?: string | null;
};

type ShareFieldKind = 'scalar' | 'array' | 'object';
type ShareTemplateKey = 'summary' | 'operations' | 'approval' | 'full' | 'custom';
type ShareActionKind = 'none' | 'approval' | 'job_action' | 'trip_action';
type JobActionKey = 'confirm' | 'approve' | 'close';
type TripActionKey = 'start' | 'complete';
type ShareAudienceProfile = 'customer_approver' | 'contractor_driver' | 'internal_ops' | 'customer_contact';

type ShareFieldOption = {
  key: string;
  label: string;
  kind: ShareFieldKind;
};

interface ShareLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entitySlug: string;
  entityId: string;
  recordTitle: string;
  record?: Record<string, unknown>;
  fields?: EntityFieldOption[];
}

const EXCLUDED_KEYS = new Set(['tenant_id', 'tenantId', '__v']);

const TEMPLATE_META: Record<Exclude<ShareTemplateKey, 'custom'>, { label: string; description: string }> = {
  summary: {
    label: 'Summary',
    description: 'Core identity, status, references, and the main business facts.',
  },
  operations: {
    label: 'Operations',
    description: 'Summary plus schedule, movement, and execution-related fields.',
  },
  approval: {
    label: 'Approval pack',
    description: 'Best for approval requests with decision context and customer-facing details.',
  },
  full: {
    label: 'Full record',
    description: 'Share the full filtered record, including non-sensitive arrays and objects.',
  },
};

function isApprovalEntity(entitySlug: string): boolean {
  return entitySlug === 'agency_approval_requests';
}

function isJobEntity(entitySlug: string): boolean {
  return entitySlug === 'jobs';
}

function isTripEntity(entitySlug: string): boolean {
  return entitySlug === 'job-trips';
}

function getSharePurposeMeta(entitySlug: string): {
  primary: string;
  description: string;
  supported: string[];
  unsupported?: string;
} {
  if (isApprovalEntity(entitySlug)) {
    return {
      primary: 'Approval request',
      description: 'Send this record out as an approval request. The recipient can approve or reject, and you can require OTP, photo, and signature.',
      supported: ['Info share', 'Approval request'],
    };
  }

  if (isJobEntity(entitySlug)) {
    return {
      primary: 'Job action',
      description: 'Share as plain information, or turn it into a customer / contractor job action such as confirm, approve, or close.',
      supported: ['Info share', 'Job action'],
    };
  }

  if (isTripEntity(entitySlug)) {
    return {
      primary: 'Trip / task assignment',
      description: 'Share trip details, or send an external task action such as start or complete with evidence and result code.',
      supported: ['Info share', 'Trip / task assignment'],
    };
  }

  return {
    primary: 'Info share',
    description: 'This record currently supports information sharing only.',
    supported: ['Info share'],
  };
}

function asObject(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}

function getShareActionType(link: ExistingShareLink): ShareActionKind {
  const type = asObject(asObject(link.metadata).action).type;
  return type === 'approval' || type === 'job_action' || type === 'trip_action' ? type : 'none';
}

function getShareActionModeLabel(link: ExistingShareLink): string {
  const action = asObject(asObject(link.metadata).action);
  const type = getShareActionType(link);
  const actionKey = typeof action.actionKey === 'string' ? action.actionKey : '';
  if (type === 'approval') return 'Approval request';
  if (type === 'job_action') {
    if (actionKey === 'confirm') return 'Job action · Confirm';
    if (actionKey === 'approve') return 'Job action · Approve';
    return 'Job action · Close';
  }
  if (type === 'trip_action') {
    return actionKey === 'start' ? 'Trip action · Start' : 'Trip action · Complete';
  }
  return 'Info share';
}

function getSupportedActionKinds(entitySlug: string): ShareActionKind[] {
  if (isApprovalEntity(entitySlug)) return ['none', 'approval'];
  if (isJobEntity(entitySlug)) return ['none', 'job_action'];
  if (isTripEntity(entitySlug)) return ['none', 'trip_action'];
  return ['none'];
}

function formatExpiry(value: string): string {
  return new Date(value).toLocaleString();
}

function buildStatus(link: ExistingShareLink): { label: string; variant: 'default' | 'secondary' | 'outline' } {
  if (!link.is_active) return { label: 'Revoked', variant: 'outline' };
  if (new Date(link.expires_at).getTime() < Date.now()) return { label: 'Expired', variant: 'outline' };
  return { label: 'Active', variant: 'secondary' };
}

function toTitleCase(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function inferFieldKind(value: unknown): ShareFieldKind {
  if (Array.isArray(value)) return 'array';
  if (value && typeof value === 'object') return 'object';
  return 'scalar';
}

function buildFieldOptions(
  record: Record<string, unknown> | undefined,
  fields: EntityFieldOption[] | undefined,
): ShareFieldOption[] {
  const fieldLabelMap = new Map<string, string>();
  for (const field of fields ?? []) {
    if (field.label && field.label.trim()) fieldLabelMap.set(field.name, field.label.trim());
  }

  const keys = Object.keys(record ?? {})
    .filter((key) => !EXCLUDED_KEYS.has(key))
    .sort((a, b) => a.localeCompare(b));

  return keys.map((key) => ({
    key,
    label: fieldLabelMap.get(key) ?? toTitleCase(key),
    kind: inferFieldKind(record?.[key]),
  }));
}

function templateKeywords(template: ShareTemplateKey, approvalEntity: boolean): RegExp {
  if (template === 'approval' && approvalEntity) {
    return /(approval|request|decision|reason|status|priority|customer|company|contact|job|shipment|reference|ref|amount|currency|valid|expiry|email|phone|pickup|delivery|origin|destination|eta|etd|description|remarks|note)/i;
  }
  if (template === 'operations') {
    return /(status|priority|job|shipment|booking|reference|ref|customer|container|vehicle|driver|pickup|delivery|origin|destination|eta|etd|date|time|schedule|trip|route|port|vessel|voyage|mode|service|commodity|weight|volume|packages|qty|quantity|remarks|instruction)/i;
  }
  return /(status|priority|job|shipment|booking|reference|ref|customer|company|contact|code|number|name|title|subject|request|amount|currency|pickup|delivery|origin|destination|eta|etd|date|remarks|description)/i;
}

function computeTemplateFields(
  template: Exclude<ShareTemplateKey, 'custom'>,
  options: ShareFieldOption[],
  approvalEntity: boolean,
): string[] {
  if (template === 'full') return options.map((field) => field.key);

  const regex = templateKeywords(template, approvalEntity);
  const scalarMatches = options
    .filter((field) => field.kind === 'scalar' && regex.test(field.key))
    .map((field) => field.key);

  const fallbackScalars = options
    .filter((field) => field.kind === 'scalar')
    .slice(0, template === 'summary' ? 10 : 16)
    .map((field) => field.key);

  const chosen = scalarMatches.length > 0 ? scalarMatches : fallbackScalars;

  if (template === 'approval' && approvalEntity) {
    const importantNonScalars = options
      .filter((field) => field.kind !== 'scalar' && /(item|charge|container|document|attachment)/i.test(field.key))
      .slice(0, 4)
      .map((field) => field.key);
    return Array.from(new Set([...chosen, ...importantNonScalars]));
  }

  return Array.from(new Set(chosen));
}

function sortKeysByTemplateOrder(allKeys: string[], selectedKeys: string[]): string[] {
  const selected = new Set(selectedKeys);
  return allKeys.filter((key) => selected.has(key));
}

function shareToWhatsApp(url: string, label: string) {
  const text = encodeURIComponent(`${label}\n${url}`);
  window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
}

function getAudienceOptions(actionType: ShareActionKind): Array<{ value: ShareAudienceProfile; label: string }> {
  if (actionType === 'approval') {
    return [
      { value: 'customer_approver', label: 'Customer approver' },
      { value: 'internal_ops', label: 'Internal manager' },
    ];
  }
  if (actionType === 'job_action') {
    return [
      { value: 'customer_contact', label: 'Customer contact' },
      { value: 'contractor_driver', label: 'Outside contractor' },
      { value: 'internal_ops', label: 'Internal ops' },
    ];
  }
  if (actionType === 'trip_action') {
    return [
      { value: 'contractor_driver', label: 'Driver / contractor' },
      { value: 'customer_contact', label: 'Receiver / customer contact' },
      { value: 'internal_ops', label: 'Internal ops' },
    ];
  }
  return [];
}

function getActionPreset(params: {
  actionType: ShareActionKind;
  jobActionKey: JobActionKey;
  tripActionKey: TripActionKey;
  audienceProfile: ShareAudienceProfile;
}): {
  title: string;
  description: string;
  submitLabel: string;
  requireActorName: boolean;
  requireActorEmail: boolean;
  allowPhoto: boolean;
  requirePhoto: boolean;
  allowAttachment: boolean;
  requireAttachment: boolean;
  allowSignature: boolean;
  requireSignature: boolean;
  otpRequired: boolean;
  allowComment: boolean;
  singleUse: boolean;
  defaultTripResult: 'success' | 'failure';
  failureCodesText: string;
} {
  if (params.actionType === 'job_action') {
    if (params.jobActionKey === 'confirm') {
      return {
        title: params.audienceProfile === 'contractor_driver' ? 'Acknowledge this job' : 'Confirm this job',
        description: params.audienceProfile === 'contractor_driver'
          ? 'Ask the assigned contractor to acknowledge the job and confirm they will execute it.'
          : 'Ask the customer or contractor to confirm the job details before execution.',
        submitLabel: 'Confirm job',
        requireActorName: true,
        requireActorEmail: params.audienceProfile !== 'contractor_driver',
        allowPhoto: false,
        requirePhoto: false,
        allowAttachment: true,
        requireAttachment: false,
        allowSignature: false,
        requireSignature: false,
        otpRequired: params.audienceProfile !== 'internal_ops',
        allowComment: true,
        singleUse: true,
        defaultTripResult: 'success',
        failureCodesText: 'FAIL_NO_SHOW, FAIL_SITE_CLOSED, FAIL_WRONG_ADDRESS',
      };
    }
    if (params.jobActionKey === 'approve') {
      return {
        title: 'Approve this job',
        description: params.audienceProfile === 'internal_ops'
          ? 'Send this job to an internal manager for operational approval.'
          : 'Send this job for approval before release, dispatch, or billing.',
        submitLabel: 'Approve job',
        requireActorName: true,
        requireActorEmail: true,
        allowPhoto: false,
        requirePhoto: false,
        allowAttachment: true,
        requireAttachment: false,
        allowSignature: false,
        requireSignature: false,
        otpRequired: params.audienceProfile !== 'internal_ops',
        allowComment: true,
        singleUse: true,
        defaultTripResult: 'success',
        failureCodesText: 'FAIL_NO_SHOW, FAIL_SITE_CLOSED, FAIL_WRONG_ADDRESS',
      };
    }
    return {
      title: 'Close this job',
      description: params.audienceProfile === 'contractor_driver'
        ? 'Ask the contractor to close the job after final proof is uploaded.'
        : 'Close the job after all work is completed and final documents are ready.',
      submitLabel: 'Close job',
      requireActorName: true,
      requireActorEmail: params.audienceProfile !== 'contractor_driver',
      allowPhoto: true,
      requirePhoto: false,
      allowAttachment: true,
      requireAttachment: true,
      allowSignature: false,
      requireSignature: false,
      otpRequired: params.audienceProfile !== 'internal_ops',
      allowComment: true,
      singleUse: true,
      defaultTripResult: 'success',
      failureCodesText: 'FAIL_NO_SHOW, FAIL_SITE_CLOSED, FAIL_WRONG_ADDRESS',
    };
  }

  if (params.actionType === 'trip_action') {
    if (params.tripActionKey === 'start') {
      return {
        title: 'Start this trip',
        description: params.audienceProfile === 'internal_ops'
          ? 'Allow internal ops to trigger the trip start remotely.'
          : 'For the assigned outside contractor or driver to acknowledge and start the trip.',
        submitLabel: 'Start trip',
        requireActorName: true,
        requireActorEmail: params.audienceProfile !== 'contractor_driver',
        allowPhoto: true,
        requirePhoto: false,
        allowAttachment: false,
        requireAttachment: false,
        allowSignature: false,
        requireSignature: false,
        otpRequired: params.audienceProfile === 'customer_contact',
        allowComment: true,
        singleUse: true,
        defaultTripResult: 'success',
        failureCodesText: 'FAIL_NO_SHOW, FAIL_SITE_CLOSED, FAIL_WRONG_ADDRESS',
      };
    }
    return {
      title: 'Complete this trip',
      description: params.audienceProfile === 'customer_contact'
        ? 'For the receiver or customer contact to confirm trip completion and upload receiving proof.'
        : 'For the contractor to complete the trip, upload POD / site proof, and choose success or failure.',
      submitLabel: 'Complete trip',
      requireActorName: true,
      requireActorEmail: params.audienceProfile !== 'contractor_driver',
      allowPhoto: true,
      requirePhoto: true,
      allowAttachment: true,
      requireAttachment: true,
      allowSignature: true,
      requireSignature: false,
      otpRequired: params.audienceProfile === 'customer_contact',
      allowComment: true,
      singleUse: true,
      defaultTripResult: 'success',
      failureCodesText: 'FAIL_NO_SHOW, FAIL_SITE_CLOSED, FAIL_WRONG_ADDRESS, FAIL_CUSTOMER_REJECTED, FAIL_ACCESS_DENIED',
    };
  }

  return {
    title: 'Approval required',
    description: params.audienceProfile === 'internal_ops'
      ? 'Ask an internal manager to review and approve this request.'
      : 'Ask the recipient to review and approve this request.',
    submitLabel: 'Submit decision',
    requireActorName: true,
    requireActorEmail: true,
    allowPhoto: true,
    requirePhoto: false,
    allowAttachment: false,
    requireAttachment: false,
    allowSignature: true,
    requireSignature: false,
    otpRequired: params.audienceProfile !== 'internal_ops',
    allowComment: true,
    singleUse: true,
    defaultTripResult: 'success',
    failureCodesText: 'FAIL_NO_SHOW, FAIL_SITE_CLOSED, FAIL_WRONG_ADDRESS',
  };
}

export function ShareLinkDialog({
  open,
  onOpenChange,
  entitySlug,
  entityId,
  recordTitle,
  record,
  fields,
}: ShareLinkDialogProps) {
  const toast = useToast();
  const approvalEntity = isApprovalEntity(entitySlug);
  const tripEntity = isTripEntity(entitySlug);
  const sharePurposeMeta = useMemo(() => getSharePurposeMeta(entitySlug), [entitySlug]);
  const supportedActionKinds = useMemo(() => getSupportedActionKinds(entitySlug), [entitySlug]);
  const fieldOptions = useMemo(() => buildFieldOptions(record, fields), [fields, record]);
  const availableFieldKeys = useMemo(() => fieldOptions.map((field) => field.key), [fieldOptions]);

  const [loading, setLoading] = useState(false);
  const [existingLinks, setExistingLinks] = useState<ExistingShareLink[]>([]);
  const [label, setLabel] = useState(recordTitle);
  const [expiresInDays, setExpiresInDays] = useState('7');
  const [maxViews, setMaxViews] = useState('');
  const [viewTemplate, setViewTemplate] = useState<ShareTemplateKey>(approvalEntity ? 'approval' : 'summary');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [includeArrays, setIncludeArrays] = useState(approvalEntity);
  const [includeObjects, setIncludeObjects] = useState(false);
  const [showEmptyFields, setShowEmptyFields] = useState(false);
  const [showActionEvidence, setShowActionEvidence] = useState(true);
  const [actionType, setActionType] = useState<ShareActionKind>(approvalEntity ? 'approval' : 'none');
  const [audienceProfile, setAudienceProfile] = useState<ShareAudienceProfile>('customer_contact');
  const [jobActionKey, setJobActionKey] = useState<JobActionKey>('confirm');
  const [tripActionKey, setTripActionKey] = useState<TripActionKey>('start');
  const [actionTitle, setActionTitle] = useState('Approval required');
  const [actionDescription, setActionDescription] = useState('');
  const [submitLabel, setSubmitLabel] = useState('Submit decision');
  const [allowComment, setAllowComment] = useState(true);
  const [requireActorName, setRequireActorName] = useState(true);
  const [requireActorEmail, setRequireActorEmail] = useState(true);
  const [allowPhoto, setAllowPhoto] = useState(false);
  const [requirePhoto, setRequirePhoto] = useState(false);
  const [allowSignature, setAllowSignature] = useState(false);
  const [requireSignature, setRequireSignature] = useState(false);
  const [allowAttachment, setAllowAttachment] = useState(false);
  const [requireAttachment, setRequireAttachment] = useState(false);
  const [singleUse, setSingleUse] = useState(true);
  const [otpRequired, setOtpRequired] = useState(true);
  const [allowedRecipientEmail, setAllowedRecipientEmail] = useState('');
  const [defaultTripResult, setDefaultTripResult] = useState<'success' | 'failure'>('success');
  const [failureCodesText, setFailureCodesText] = useState('FAIL_NO_SHOW, FAIL_SITE_CLOSED, FAIL_WRONG_ADDRESS');
  const [creating, setCreating] = useState(false);
  const [createdShareUrl, setCreatedShareUrl] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const canCreateAction = actionType !== 'none';
  const missingOtpRecipientEmail = canCreateAction && otpRequired && !allowedRecipientEmail.trim();

  useEffect(() => {
    if (!open) return;
    setCreatedShareUrl(null);
    void loadLinks();
  }, [open, entitySlug, entityId]);

  useEffect(() => {
    if (!open) return;
    const initialTemplate: Exclude<ShareTemplateKey, 'custom'> = approvalEntity ? 'approval' : 'summary';
    const initialSelection = computeTemplateFields(initialTemplate, fieldOptions, approvalEntity);
    setViewTemplate(initialTemplate);
    setSelectedFields(sortKeysByTemplateOrder(availableFieldKeys, initialSelection));
    setIncludeArrays(approvalEntity);
    setIncludeObjects(false);
    setShowEmptyFields(false);
    setShowActionEvidence(true);
    setActionType(approvalEntity ? 'approval' : 'none');
    setAudienceProfile(approvalEntity ? 'customer_approver' : tripEntity ? 'contractor_driver' : 'customer_contact');
    setJobActionKey('confirm');
    setTripActionKey('start');
    setAllowPhoto(false);
    setRequireAttachment(false);
    setAllowAttachment(false);
    setAllowSignature(false);
    setDefaultTripResult('success');
    setLabel(recordTitle);
  }, [open, approvalEntity, availableFieldKeys, fieldOptions, recordTitle, tripEntity]);

  useEffect(() => {
    if (!canCreateAction) return;
    const preset = getActionPreset({ actionType, jobActionKey, tripActionKey, audienceProfile });
    setActionTitle(preset.title);
    setActionDescription(preset.description);
    setSubmitLabel(preset.submitLabel);
    setAllowComment(preset.allowComment);
    setRequireActorName(preset.requireActorName);
    setRequireActorEmail(preset.requireActorEmail);
    setAllowPhoto(preset.allowPhoto);
    setRequirePhoto(preset.requirePhoto);
    setAllowAttachment(preset.allowAttachment);
    setRequireAttachment(preset.requireAttachment);
    setAllowSignature(preset.allowSignature);
    setRequireSignature(preset.requireSignature);
    setOtpRequired(preset.otpRequired);
    setSingleUse(preset.singleUse);
    setDefaultTripResult(preset.defaultTripResult);
    setFailureCodesText(preset.failureCodesText);
  }, [actionType, audienceProfile, canCreateAction, jobActionKey, tripActionKey]);

  useEffect(() => {
    if (!allowPhoto && requirePhoto) setRequirePhoto(false);
  }, [allowPhoto, requirePhoto]);

  useEffect(() => {
    if (!allowAttachment && requireAttachment) setRequireAttachment(false);
  }, [allowAttachment, requireAttachment]);

  useEffect(() => {
    if (!allowSignature && requireSignature) setRequireSignature(false);
  }, [allowSignature, requireSignature]);

  async function loadLinks() {
    setLoading(true);
    try {
      const data = await api.get<ShareLinksPage>(`/share-links?entitySlug=${encodeURIComponent(entitySlug)}&limit=100`);
      setExistingLinks((data.items ?? []).filter((item) => item.entity_id === entityId));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load share links');
    } finally {
      setLoading(false);
    }
  }

  function applyTemplate(nextTemplate: Exclude<ShareTemplateKey, 'custom'>) {
    const nextSelection = computeTemplateFields(nextTemplate, fieldOptions, approvalEntity);
    setViewTemplate(nextTemplate);
    setSelectedFields(sortKeysByTemplateOrder(availableFieldKeys, nextSelection));
    if (nextTemplate === 'full') {
      setIncludeArrays(true);
      setIncludeObjects(true);
      setShowEmptyFields(false);
    } else if (nextTemplate === 'approval') {
      setIncludeArrays(true);
    }
  }

  function toggleField(fieldKey: string, checked: boolean) {
    setViewTemplate('custom');
    setSelectedFields((current) => {
      if (checked) {
        return sortKeysByTemplateOrder(availableFieldKeys, [...current, fieldKey]);
      }
      return current.filter((key) => key !== fieldKey);
    });
  }

  const selectedFieldSet = useMemo(() => new Set(selectedFields), [selectedFields]);
  const parsedFailureCodes = useMemo(() => failureCodesText
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean), [failureCodesText]);

  const actionMetadata = useMemo(() => {
    if (!canCreateAction) return undefined;
    const base = {
      title: actionTitle.trim() || (
        actionType === 'approval'
          ? 'Approval required'
          : actionType === 'job_action'
            ? 'Job action required'
            : 'Trip action required'
      ),
      description: actionDescription.trim() || undefined,
      submitLabel: submitLabel.trim() || 'Submit action',
      audienceProfile,
      allowComment,
      requireActorName,
      requireActorEmail,
      allowPhoto,
      requirePhoto,
      allowSignature,
      requireSignature,
      allowAttachment,
      requireAttachment,
      singleUse,
      otpRequired,
      allowedRecipientEmail: allowedRecipientEmail.trim() || undefined,
    };

    if (actionType === 'approval') {
      return {
        action: {
          ...base,
          type: 'approval',
        },
      };
    }

    if (actionType === 'job_action') {
      return {
        action: {
          ...base,
          type: 'job_action',
          actionKey: jobActionKey,
        },
      };
    }

    return {
      action: {
        ...base,
        type: 'trip_action',
        actionKey: tripActionKey,
        defaultResult: defaultTripResult,
        failureCodes: tripActionKey === 'complete' ? parsedFailureCodes : undefined,
      },
    };
  }, [
    actionDescription,
    actionTitle,
    actionType,
    audienceProfile,
    allowComment,
    canCreateAction,
    defaultTripResult,
    failureCodesText,
    jobActionKey,
    otpRequired,
    allowedRecipientEmail,
    allowAttachment,
    allowPhoto,
    allowSignature,
    parsedFailureCodes,
    requireAttachment,
    requireActorEmail,
    requireActorName,
    requirePhoto,
    requireSignature,
    singleUse,
    submitLabel,
    tripActionKey,
  ]);

  const viewMetadata = useMemo(() => ({
    view: {
      templateKey: viewTemplate,
      visibleFields: selectedFields,
      includeArrays,
      includeObjects,
      showEmptyFields,
      showActionEvidence,
    },
  }), [includeArrays, includeObjects, selectedFields, showActionEvidence, showEmptyFields, viewTemplate]);

  async function handleCreate() {
    if (selectedFields.length === 0) {
      toast.error('Select at least one field to share');
      return;
    }

    if (missingOtpRecipientEmail) {
      toast.error('Recipient email is required when OTP protection is enabled');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        entitySlug,
        entityId,
        label: label.trim() || recordTitle,
        expiresInDays: Math.max(1, Math.min(90, Number(expiresInDays) || 7)),
        maxViews: maxViews.trim() ? Math.max(1, Number(maxViews)) : undefined,
        metadata: {
          ...viewMetadata,
          ...(actionMetadata ?? {}),
        },
      };

      const data = await api.post<CreateShareResponse>('/share-links', payload);
      setCreatedShareUrl(data.shareUrl);
      await navigator.clipboard.writeText(data.shareUrl);
      toast.success('Share link created and copied');
      await loadLinks();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create share link');
    } finally {
      setCreating(false);
    }
  }

  async function handleCopy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied');
    } catch {
      toast.error('Failed to copy share link');
    }
  }

  async function handleRevoke(id: string) {
    setRevokingId(id);
    try {
      await api.delete(`/share-links/${encodeURIComponent(id)}`);
      toast.success('Share link revoked');
      await loadLinks();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke share link');
    } finally {
      setRevokingId(null);
    }
  }

  const scalarCount = fieldOptions.filter((field) => field.kind === 'scalar').length;
  const arrayCount = fieldOptions.filter((field) => field.kind === 'array').length;
  const objectCount = fieldOptions.filter((field) => field.kind === 'object').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88vh] w-[96vw] max-w-7xl flex-col overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b bg-background px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            Public Share
          </DialogTitle>
          <DialogDescription>
            Create a share.boox.app link with a clear purpose. You can start from a template, then manually hide fields, sublists, objects, and action evidence.
          </DialogDescription>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 gap-6 px-6 py-5 lg:grid-cols-[minmax(0,1.65fr)_320px] xl:grid-cols-[minmax(0,1.85fr)_340px]">
          <div className="min-w-0 overflow-y-auto pr-2 space-y-5">
            <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">Share purpose</span>
                <Badge>{sharePurposeMeta.primary}</Badge>
                {sharePurposeMeta.supported.map((mode) => (
                  <Badge key={mode} variant="outline">{mode}</Badge>
                ))}
              </div>
              <div className="text-sm text-muted-foreground">{sharePurposeMeta.description}</div>
              <div className="grid gap-3 sm:grid-cols-[220px_1fr]">
                <div className="space-y-2">
                  <Label>Mode</Label>
                  <Select value={actionType} onValueChange={(value) => setActionType(value as ShareActionKind)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose share mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Info share</SelectItem>
                      {supportedActionKinds.includes('approval') ? <SelectItem value="approval">Approval request</SelectItem> : null}
                      {supportedActionKinds.includes('job_action') ? <SelectItem value="job_action">Job action</SelectItem> : null}
                      {supportedActionKinds.includes('trip_action') ? <SelectItem value="trip_action">Trip / task assignment</SelectItem> : null}
                    </SelectContent>
                  </Select>
                </div>
                <div className="rounded-md border bg-background px-3 py-2 text-xs text-muted-foreground">
                  {actionType === 'none' && 'Use this when the recipient should only view selected information.'}
                  {actionType === 'approval' && 'Use this when the recipient must approve or reject the request.'}
                  {actionType === 'job_action' && 'Use this when the recipient should confirm, approve, or close the job.'}
                  {actionType === 'trip_action' && 'Use this for outside contractor trip actions such as start, complete, result code, and evidence upload.'}
                </div>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="share-label">Share label</Label>
                <Input id="share-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={recordTitle} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="share-expiry">Expires in days</Label>
                <Input id="share-expiry" type="number" min={1} max={90} value={expiresInDays} onChange={(e) => setExpiresInDays(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="share-maxviews">Max views</Label>
                <Input id="share-maxviews" type="number" min={1} placeholder="Unlimited" value={maxViews} onChange={(e) => setMaxViews(e.target.value)} />
              </div>
            </div>

            <div className="rounded-lg border p-4 space-y-4">
                <div className="space-y-1">
                  <div className="text-sm font-medium">What should the recipient see?</div>
                  <div className="text-xs text-muted-foreground">
                    This controls an information share. Start from a template, then manually turn fields on or off.
                  </div>
                </div>

              <div className="grid gap-4 sm:grid-cols-[220px_1fr]">
                <div className="space-y-2">
                  <Label>Template</Label>
                  <Select
                    value={viewTemplate}
                    onValueChange={(value) => {
                      if (value === 'custom') {
                        setViewTemplate('custom');
                        return;
                      }
                      applyTemplate(value as Exclude<ShareTemplateKey, 'custom'>);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="summary">{TEMPLATE_META.summary.label}</SelectItem>
                      <SelectItem value="operations">{TEMPLATE_META.operations.label}</SelectItem>
                      {approvalEntity ? <SelectItem value="approval">{TEMPLATE_META.approval.label}</SelectItem> : null}
                      <SelectItem value="full">{TEMPLATE_META.full.label}</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                  {viewTemplate === 'custom'
                    ? 'Custom mode. The checked fields below define exactly what the recipient will see.'
                    : TEMPLATE_META[viewTemplate as Exclude<ShareTemplateKey, 'custom'>].description}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">{selectedFields.length} fields selected</Badge>
                <Badge variant="outline">{scalarCount} scalar</Badge>
                <Badge variant="outline">{arrayCount} sublists</Badge>
                <Badge variant="outline">{objectCount} objects</Badge>
              </div>

              <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-4">
                <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm">
                  <Checkbox checked={includeArrays} onCheckedChange={(checked) => setIncludeArrays(checked === true)} />
                  <span>Include sublists / array fields</span>
                </label>
                <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm">
                  <Checkbox checked={includeObjects} onCheckedChange={(checked) => setIncludeObjects(checked === true)} />
                  <span>Include object / nested blocks</span>
                </label>
                <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm">
                  <Checkbox checked={showEmptyFields} onCheckedChange={(checked) => setShowEmptyFields(checked === true)} />
                  <span>Show empty fields</span>
                </label>
                <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm">
                  <Checkbox checked={showActionEvidence} onCheckedChange={(checked) => setShowActionEvidence(checked === true)} />
                  <span>Show submitted photo / signature evidence</span>
                </label>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-medium">Field selection</div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setViewTemplate('custom');
                        setSelectedFields(availableFieldKeys);
                      }}
                    >
                      Select all
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setViewTemplate('custom');
                        setSelectedFields([]);
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto rounded-lg border">
                  <div className="grid gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {fieldOptions.length === 0 ? (
                      <div className="text-sm text-muted-foreground md:col-span-4">No fields available from the current record.</div>
                    ) : fieldOptions.map((field) => (
                      <label key={field.key} className="flex min-h-[76px] items-start gap-2 rounded-md border p-2 text-sm">
                        <Checkbox
                          checked={selectedFieldSet.has(field.key)}
                          onCheckedChange={(checked) => toggleField(field.key, checked === true)}
                        />
                        <span className="min-w-0">
                          <span className="block font-medium">{field.label}</span>
                          <span className="block break-all text-xs text-muted-foreground">
                            {field.key} · {field.kind}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {canCreateAction ? (
              <div className="rounded-lg border p-4 space-y-4">
                <div className="space-y-1">
                  <div>
                    <div className="text-sm font-medium">
                      {actionType === 'approval' && 'Approval request'}
                      {actionType === 'job_action' && 'Job action'}
                      {actionType === 'trip_action' && 'Trip / task assignment'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {actionType === 'approval' && 'Turn this share into an external approval request with approve / reject, OTP, photo, and signature.'}
                      {actionType === 'job_action' && 'Use this when the recipient should confirm, approve, or close the job.'}
                      {actionType === 'trip_action' && 'Use this when the recipient should start or complete the trip, then upload evidence.'}
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Intended for</Label>
                    <Select value={audienceProfile} onValueChange={(value) => setAudienceProfile(value as ShareAudienceProfile)}>
                      <SelectTrigger><SelectValue placeholder="Choose audience" /></SelectTrigger>
                      <SelectContent>
                        {getAudienceOptions(actionType).map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {actionType === 'job_action' ? (
                    <div className="space-y-2">
                      <Label>Job action</Label>
                      <Select value={jobActionKey} onValueChange={(value) => setJobActionKey(value as JobActionKey)}>
                        <SelectTrigger><SelectValue placeholder="Choose job action" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="confirm">Confirm</SelectItem>
                          <SelectItem value="approve">Approve</SelectItem>
                          <SelectItem value="close">Close</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}

                  {actionType === 'trip_action' ? (
                    <div className="space-y-2">
                      <Label>Trip action</Label>
                      <Select value={tripActionKey} onValueChange={(value) => setTripActionKey(value as TripActionKey)}>
                        <SelectTrigger><SelectValue placeholder="Choose trip action" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="start">Start</SelectItem>
                          <SelectItem value="complete">Complete</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}

                  {actionType === 'trip_action' && tripActionKey === 'complete' ? (
                    <div className="space-y-2">
                      <Label>Default result</Label>
                      <Select value={defaultTripResult} onValueChange={(value) => setDefaultTripResult(value as 'success' | 'failure')}>
                        <SelectTrigger><SelectValue placeholder="Choose result" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="success">Success</SelectItem>
                          <SelectItem value="failure">Failure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}
                </div>

                {actionType === 'trip_action' && tripActionKey === 'complete' ? (
                  <div className="space-y-2">
                    <Label htmlFor="failure-codes">Failure codes</Label>
                    <Input
                      id="failure-codes"
                      value={failureCodesText}
                      onChange={(e) => setFailureCodesText(e.target.value)}
                      placeholder="FAIL_NO_SHOW, FAIL_SITE_CLOSED"
                    />
                    <div className="text-xs text-muted-foreground">
                      Comma-separated. The recipient only needs to pick one when result is failure.
                    </div>
                  </div>
                ) : null}

                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="action-title">Action title</Label>
                      <Input id="action-title" value={actionTitle} onChange={(e) => setActionTitle(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="submit-label">Submit label</Label>
                      <Input id="submit-label" value={submitLabel} onChange={(e) => setSubmitLabel(e.target.value)} />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="action-description">Description</Label>
                      <Textarea id="action-description" rows={3} value={actionDescription} onChange={(e) => setActionDescription(e.target.value)} placeholder="What should the recipient do from this share?" />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Action rules</div>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm"><Checkbox checked={allowComment} onCheckedChange={(checked) => setAllowComment(checked === true)} /><span>Allow comment</span></label>
                      <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm"><Checkbox checked={requireActorName} onCheckedChange={(checked) => setRequireActorName(checked === true)} /><span>Require actor name</span></label>
                      <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm"><Checkbox checked={requireActorEmail} onCheckedChange={(checked) => setRequireActorEmail(checked === true)} /><span>Require actor email</span></label>
                      <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm"><Checkbox checked={allowPhoto} onCheckedChange={(checked) => setAllowPhoto(checked === true)} /><span>Allow photo upload</span></label>
                      <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm"><Checkbox checked={requirePhoto} disabled={!allowPhoto} onCheckedChange={(checked) => { if (checked === true) setAllowPhoto(true); setRequirePhoto(checked === true); }} /><span>Require photo upload</span></label>
                      <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm"><Checkbox checked={allowAttachment} onCheckedChange={(checked) => setAllowAttachment(checked === true)} /><span>Allow attachment upload</span></label>
                      <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm"><Checkbox checked={requireAttachment} disabled={!allowAttachment} onCheckedChange={(checked) => { if (checked === true) setAllowAttachment(true); setRequireAttachment(checked === true); }} /><span>Require attachment file</span></label>
                      <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm"><Checkbox checked={allowSignature} onCheckedChange={(checked) => setAllowSignature(checked === true)} /><span>Allow signature</span></label>
                      <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm"><Checkbox checked={requireSignature} disabled={!allowSignature} onCheckedChange={(checked) => { if (checked === true) setAllowSignature(true); setRequireSignature(checked === true); }} /><span>Require signature</span></label>
                      <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm"><Checkbox checked={singleUse} onCheckedChange={(checked) => setSingleUse(checked === true)} /><span>Single use</span></label>
                      <label className="flex items-start gap-2 rounded-md border bg-muted/20 p-3 text-sm"><Checkbox checked={otpRequired} onCheckedChange={(checked) => setOtpRequired(checked === true)} /><span>Recipient OTP gate</span></label>
                    </div>
                  </div>

                  <div className="rounded-lg border bg-muted/10 p-4 space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Access control</div>
                    <Label htmlFor="recipient-email">Recipient email</Label>
                    <Input
                      id="recipient-email"
                      type="email"
                      value={allowedRecipientEmail}
                      onChange={(e) => setAllowedRecipientEmail(e.target.value)}
                      placeholder={otpRequired ? 'recipient@company.com' : 'Optional'}
                    />
                    {missingOtpRecipientEmail ? (
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        OTP protection is on. Fill in the recipient email here, or turn off <span className="font-semibold">Recipient OTP gate</span>.
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground">
                        {otpRequired
                          ? 'The share recipient will receive a one-time verification code by email before they can open the action.'
                          : 'Leave empty for an open link, or fill in one email to target a specific recipient.'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : null}

            {createdShareUrl ? (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  Latest share link
                </div>
                <div className="rounded-md bg-background px-3 py-2 text-sm break-all">{createdShareUrl}</div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => void handleCopy(createdShareUrl)}>
                    <Copy className="mr-1.5 h-3.5 w-3.5" />
                    Copy
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => shareToWhatsApp(createdShareUrl, label.trim() || recordTitle)}>
                    <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
                    WhatsApp
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(createdShareUrl, '_blank', 'noopener,noreferrer')}>
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                    Open
                  </Button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="min-w-0 overflow-y-auto border-t pt-4 lg:max-w-[340px] lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Existing links</div>
              {loading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
            </div>

            <div className="space-y-3">
              {existingLinks.length === 0 && !loading ? (
                <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                  No share links yet for this record.
                </div>
              ) : null}

              {existingLinks.map((link) => {
                const status = buildStatus(link);
                const shareUrl = `https://share.boox.app/e/${link.token ?? ''}`;
                return (
                  <div key={link.id} className="rounded-lg border p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <div className="text-sm font-medium">{link.label ?? recordTitle}</div>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant={status.variant}>{status.label}</Badge>
                          <Badge variant="outline">{getShareActionModeLabel(link)}</Badge>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2 text-destructive"
                        onClick={() => void handleRevoke(link.id)}
                        disabled={revokingId === link.id || !link.is_active}
                      >
                        {revokingId === link.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Expires {formatExpiry(link.expires_at)} · Views {link.view_count}{link.max_views ? `/${link.max_views}` : ''}
                    </div>
                    {link.token ? (
                      <>
                        <div className="rounded-md bg-muted/40 px-3 py-2 text-xs break-all">{shareUrl}</div>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => void handleCopy(shareUrl)}>
                            <Copy className="mr-1.5 h-3.5 w-3.5" />
                            Copy
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => shareToWhatsApp(shareUrl, link.label ?? recordTitle)}>
                            <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
                            WhatsApp
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => window.open(shareUrl, '_blank', 'noopener,noreferrer')}>
                            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                            Open
                          </Button>
                        </div>
                      </>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t bg-background px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={() => void handleCreate()} disabled={creating || missingOtpRecipientEmail}>
            {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Share2 className="mr-2 h-4 w-4" />}
            Create share link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
